import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppBar, Toolbar, IconButton, Drawer, ListItemButton, CssBaseline, Box, Button, Stack, Container } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuIcon from "@mui/icons-material/Menu";
import { useCart } from './contexts/CartContext.jsx';

const Nav = props => {
  const {
    brand,
    header,
    navLinks,
    cta
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { cart } = useCart();
  const cartCount = cart?.items?.length || 0;

  const [drawerOpen, setDrawerOpen] = useState(false);

  const computedLinks = useMemo(() => {
    return navLinks.map(link => {
      if (link.path === '/cart' && cartCount > 0) {
        return { ...link, text: `Cart (${cartCount})` };
      }
      return link;
    });
  }, [navLinks, cartCount]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const gotoLink = navLink => {
    if (!navLink) return;

    if (navLink.scrollTo) {
      if (typeof window === 'undefined') return;
      const scrollAction = () => {
        const el = document.getElementById(navLink.scrollTo);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      };

      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(scrollAction, 100);
      } else {
        scrollAction();
      }
      return;
    }

    if (navLink.path) {
      navigate(navLink.path);
      return;
    }

    if (navLink.href) {
      window.open(navLink.href, navLink.target || '_blank');
    }
  }

  const toolbarPadding = { xs: '16px', sm: '24px', md: '40px' };
  const toolbarStyle = {
    px: toolbarPadding,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  };
  const logoHeight = { xs: 56, sm: 72, md: 90 };

  const renderLinkButton = (link, index, variant = "text") => (
    <Button
      key={index}
      variant={variant}
      endIcon={link.endIcon}
      onClick={() => gotoLink(link)}
      sx={{ color: variant === 'text' ? '#fff' : undefined }}
    >
      {link.text}
    </Button>
  );

  let nav = null;
  const enableMobileNav = true;
  if (enableMobileNav && isMobile) {
    nav = (
      <>
        <IconButton
          edge="end"
          color="inherit"
          aria-label="menu"
          onClick={handleDrawerToggle}
        >
          <MenuIcon />
        </IconButton>
        <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerToggle}>
          <Box sx={{ width: 260, p: 3 }}>
            <Stack spacing={1.5}>
              {computedLinks.map((link, index) => (
                <ListItemButton key={index} onClick={() => { gotoLink(link); handleDrawerToggle(); }}>
                  {link.text}
                </ListItemButton>
              ))}
              {cta && (
                <Button variant="contained" color="primary" onClick={() => gotoLink(cta)}>
                  {cta.text}
                </Button>
              )}
            </Stack>
          </Box>
        </Drawer>
      </>
    )
  } else {
    nav = (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ marginLeft: 'auto' }}>
        {computedLinks.map(renderLinkButton)}
        {cta && (
          <Button variant="contained" color="secondary" onClick={() => gotoLink(cta)} sx={{ borderRadius: 999, px: 3 }}>
            {cta.text}
          </Button>
        )}
      </Stack>
    )
  }

  return (
    <>
      <CssBaseline />
      <AppBar position="static" sx={{ background: 'rgba(0, 58, 39, 0.92)', backdropFilter: 'blur(6px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Toolbar disableGutters sx={{ minHeight: { xs: 80, md: 120 } }}>
          <Container maxWidth="lg" sx={toolbarStyle}>
            <Box component="a" href="/" sx={{ display: 'flex', alignItems: 'center', color: 'inherit', textDecoration: 'none', flexGrow: 1 }}>
              <Box
                component="img"
                src="/logo.webp"
                alt={`${brand} logo`}
                sx={{ height: logoHeight, width: 'auto', display: 'block' }}
              />
            </Box>
            {nav}
          </Container>
        </Toolbar>
      </AppBar>
    </>
  )
}

export default Nav;
